import { inject } from '@angular/core';
import { CanActivateFn, ResolveFn, Router } from '@angular/router';

import { RouteString } from '@lyra/app/core/enums/route-string';
import { AuthStatusCookieService } from '@lyra/app/core/services/auth-status-cookie.service';
import { SSRService } from '@lyra/app/core/services/ssr.service';
import { TokenService } from '@lyra/app/core/services/token.service';

/**
 * Ali-flow session guard — every step past the offer (order / login / questionnaire / survey-passed)
 * needs the session token that {@link questionnaireResolver} sets on the offer page, so the funnel
 * can't be deep-linked. No token → back to the variant landing. `homePath` is the variant landing to
 * fall back to (ALI_PAGE_VARIANT_A / _B). SSR is allowed through; the browser re-checks after hydration.
 */
export const aliTokenGuard =
  (homePath: RouteString): CanActivateFn =>
  (route) => {
    const _ssr = inject(SSRService);
    const _token = inject(TokenService);
    const _router = inject(Router);

    if (!_ssr.isBrowserOnly || _token.token) {
      return true;
    }
    return _router.createUrlTree(['/', route.params['lang'], homePath]);
  };

/**
 * Ali-flow real-user guard — the questionnaire additionally needs a *real* (logged-in) user, not just
 * the anonymous offer token. `auth_real_user` is set on confirm-code success; until then the user must
 * log in first. Not a real user → back to the variant landing. SSR is allowed through.
 */
export const aliRealUserGuard =
  (homePath: RouteString): CanActivateFn =>
  (route) => {
    const _ssr = inject(SSRService);
    const _authStatus = inject(AuthStatusCookieService);
    const _router = inject(Router);

    if (!_ssr.isBrowserOnly || _authStatus.isSuccess()) {
      return true;
    }
    return _router.createUrlTree(['/', route.params['lang'], homePath]);
  };

/**
 * Clears the session on the terminal survey-passed page (browser only) so the flow can't be re-entered:
 * removes the token cookie and the real-user marker.
 */
export const clearAliSessionResolver: ResolveFn<boolean> = () => {
  const _ssr = inject(SSRService);
  if (_ssr.isBrowserOnly) {
    inject(TokenService).remove();
    inject(AuthStatusCookieService).clear();
  }
  return true;
};
